import React from "react";
import { FileType, LinkType } from "../../types/baseTypes";
import { Button, ButtonTypes } from "../Global/Button/Button";
import { customSanitize } from "../../utils/customSanitize";
import { downloadFile } from "../../utils/downloadFile";
import { ReactSVG } from "react-svg";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import styles from "./ExtendedLink.module.scss";
import { useAppContext } from "../../context/AppContext";

export type DowloadButtonType = {
  link_target: FileType | false;
  link_text: string;
};

export type ExtendedLinkType = {
  type: "email" | "link" | "download" | "popup";
  link: LinkType;
  download: DowloadButtonType;
  email: {
    link_target: string;
    link_text: string;
  };
  pop_up: {
    link_text: string;
    link_aria_label: string;
  };
};

interface ExtededLinkProps {
  extendedLink: ExtendedLinkType;
  extraClass?: string;
  iconOnly?: boolean;
  btnType: ButtonTypes;
}

export const ExtendedLink = ({
  extendedLink,
  extraClass = "",
  iconOnly = false,
  btnType = "primary",
}: ExtededLinkProps) => {
  const { type, link, email, download, pop_up } = extendedLink;
  const { getIcon, getLabel } = useGlobalContext();
  const { setopenPopup } = useAppContext();
  const ariaLabelDownload =
    download?.link_target && download.link_target.url
      ? getLabel("arialabel_download_slider").replace(
          `"$filename"`,
          (download.link_target as FileType).filename,
        )
      : "";

  const renderButtonContent = (text: string) => (
    <span
      dangerouslySetInnerHTML={{
        __html: customSanitize(text),
      }}
    />
  );

  const handleDownload = (fileUrl: string) => {
    downloadFile(fileUrl).catch((e) => {
      console.error("Download failed:", e);
      // Handle error if needed
    });
  };

  const renderDownloadButton = () =>
    download?.link_target &&
    download.link_target.url && (
      <Button
        type={btnType}
        href=""
        target=""
        isDownload
        className={`${extraClass} ${styles["download-btn"]}`}
        aria-label={ariaLabelDownload}
        onClick={() => handleDownload((download.link_target as FileType).url)}
      >
        {renderButtonContent(download.link_text)}
      </Button>
    );

  const renderDownloadIcon = () =>
    download?.link_target &&
    download.link_target.url && (
      <button
        type="button"
        className={`${styles["download-link-icon"]} ${extraClass}`}
        aria-label={ariaLabelDownload}
        onClick={() => handleDownload((download.link_target as FileType).url)}
      >
        <ReactSVG
          src={getIcon("download")}
          wrapper="svg"
          width={38}
          height={38}
          aria-hidden
          beforeInjection={(svg) => {
            svg.setAttribute("alt", `Download Icon`);
            svg.setAttribute("aria-label", `Download Icon`);
          }}
        />
      </button>
    );

  const renderPopupButton = () => (
    <Button
      type={btnType}
      href=""
      target=""
      className={extraClass}
      aria-label={pop_up?.link_aria_label}
      aria-haspopup="dialog"
      onClick={() => {
        setopenPopup(true);
      }}
    >
      {renderButtonContent(pop_up.link_text)}
    </Button>
  );

  const buttonMapping = {
    link: link && (
      <Button
        type={btnType}
        href={link.url}
        target={link.target}
        aria-label={link.aria_label || link.title}
        className={extraClass}
      >
        {renderButtonContent(link.title)}
      </Button>
    ),
    email: email && (
      <Button
        type={btnType}
        href={`mailto:${email.link_target}`}
        target="_blank"
        className={extraClass}
        aria-label={getLabel("email_title").replace(
          `"$emailadress"`,
          email.link_target,
        )}
      >
        {renderButtonContent(email.link_text)}
      </Button>
    ),
    download: iconOnly ? renderDownloadIcon() : renderDownloadButton(),
    popup: renderPopupButton(),
  };

  return <>{buttonMapping[type]}</>;
};
